import { ReactNode, createContext, useState, useEffect } from "react";
import { IColumn, IColumnMap, IItem } from "./types";
import { api, PBItem, PBColumn } from "./pocketbase";

interface BoardState {
  columns: IColumnMap;
  ordered: string[]; // column ids
}

interface BoardContextType {
  boardState: BoardState;
  setBoardState: React.Dispatch<React.SetStateAction<BoardState>>;
  addColumn: () => Promise<void>;
  changeColumnTitle: (columnId: string, title: string) => Promise<void>;
  addItem: (columnId: string) => Promise<void>;
  changeItem: (
    columnId: string,
    itemIndex: number,
    content: string
  ) => Promise<void>;
}

export const BoardContext = createContext<BoardContextType>({
  boardState: {} as BoardState,
  setBoardState: {} as React.Dispatch<React.SetStateAction<BoardState>>,
  addColumn: async () => {},
  changeColumnTitle: async () => {},
  addItem: async () => {},
  changeItem: async () => {},
});

interface BoardProps {
  children: ReactNode;
}

export const BoardProvider = ({ children }: BoardProps) => {
  const [boardState, setBoardState] = useState<BoardState>({
    columns: {},
    ordered: [],
  });

  // Load initial data from PocketBase
  useEffect(() => {
    const loadData = async () => {
      try {
        const [columns, items] = await Promise.all([
          api.getColumns(),
          api.getItems(),
        ]);

        // Convert columns to IColumnMap format
        const columnMap: IColumnMap = {};
        const ordered: string[] = [];

        columns.forEach((col: PBColumn) => {
          columnMap[col.id] = {
            id: col.id,
            title: col.title,
            items: [],
          };
          ordered.push(col.id);
        });

        // Sort columns by order
        ordered.sort((a, b) => {
          const colA = columns.find((c) => c.id === a);
          const colB = columns.find((c) => c.id === b);
          return (colA?.order || 0) - (colB?.order || 0);
        });

        // Add items to their respective columns
        items.forEach((item: PBItem) => {
          if (columnMap[item.column]) {
            columnMap[item.column].items.push({
              id: item.id,
              content: item.content,
            });
          }
        });

        // Sort items by order within each column
        Object.values(columnMap).forEach((column) => {
          column.items.sort((a, b) => {
            const itemA = items.find((i) => i.id === a.id);
            const itemB = items.find((i) => i.id === b.id);
            return (itemA?.order || 0) - (itemB?.order || 0);
          });
        });

        setBoardState({ columns: columnMap, ordered });
      } catch (error) {
        console.error("Error loading data:", error);
      }
    };

    loadData();
  }, []);

  const addColumn = async () => {
    try {
      const newColumn = await api.createColumn("Untitled");
      setBoardState((prevState) => ({
        ...prevState,
        columns: {
          ...prevState.columns,
          [newColumn.id]: {
            id: newColumn.id,
            title: newColumn.title,
            items: [],
          },
        },
        ordered: [...prevState.ordered, newColumn.id],
      }));
    } catch (error) {
      console.error("Error adding column:", error);
    }
  };

  const changeColumnTitle = async (columnId: string, title: string) => {
    try {
      await api.updateColumn(columnId, { title });
      setBoardState((prevState) => ({
        ...prevState,
        columns: {
          ...prevState.columns,
          [columnId]: { ...prevState.columns[columnId], title },
        },
      }));
    } catch (error) {
      console.error("Error updating column title:", error);
    }
  };

  const addItem = async (columnId: string) => {
    try {
      const newItem = await api.createItem("Untitled", columnId);
      setBoardState((prevState) => ({
        ...prevState,
        columns: {
          ...prevState.columns,
          [columnId]: {
            ...prevState.columns[columnId],
            items: [
              ...prevState.columns[columnId].items,
              { id: newItem.id, content: newItem.content },
            ],
          },
        },
      }));
    } catch (error) {
      console.error("Error adding item:", error);
    }
  };

  const changeItem = async (
    columnId: string,
    itemIndex: number,
    content: string
  ) => {
    try {
      const item = boardState.columns[columnId].items[itemIndex];
      await api.updateItem(item.id, { content });
      setBoardState((prevState) => {
        const column = prevState.columns[columnId];
        const items = [...column.items];
        items[itemIndex] = { ...items[itemIndex], content };

        return {
          ...prevState,
          columns: {
            ...prevState.columns,
            [columnId]: {
              ...column,
              items,
            },
          },
        };
      });
    } catch (error) {
      console.error("Error updating item:", error);
    }
  };

  const value: BoardContextType = {
    boardState,
    setBoardState,
    addColumn,
    changeColumnTitle,
    addItem,
    changeItem,
  };

  return (
    <BoardContext.Provider value={value}>{children}</BoardContext.Provider>
  );
};
